import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import React from "react";
import { cn } from "../../utils/cn";

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "success" | "error" | "warning" | "info";
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  /** Ação opcional (ex.: "Tentar novamente"), renderizada abaixo do texto. */
  action?: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
  className,
  variant = "info",
  title,
  dismissible = false,
  onDismiss,
  icon,
  action,
  children,
  ...props
}) => {
  const variantStyles = {
    success: {
      container: "bg-green-50 border-green-200",
      iconBg: "bg-green-600",
      title: "text-green-900",
      description: "text-green-700",
      dismiss: "text-green-500 hover:bg-green-100",
    },
    error: {
      container: "bg-red-50 border-red-200",
      iconBg: "bg-red-500",
      title: "text-red-900",
      description: "text-red-600",
      dismiss: "text-red-500 hover:bg-red-100",
    },
    warning: {
      container: "bg-amber-50 border-amber-200",
      iconBg: "bg-amber-500",
      title: "text-amber-900",
      description: "text-amber-700",
      dismiss: "text-amber-500 hover:bg-amber-100",
    },
    info: {
      container: "bg-blue-50 border-blue-200",
      iconBg: "bg-blue-600",
      title: "text-blue-900",
      description: "text-blue-700",
      dismiss: "text-blue-500 hover:bg-blue-100",
    },
  };

  const defaultIcons = {
    success: <CheckCircle className="h-5 w-5" />,
    error: <XCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    info: <Info className="h-5 w-5" />,
  };

  const styles = variantStyles[variant];
  const displayIcon = icon || defaultIcons[variant];

  return (
    <div
      className={cn(
        "relative flex items-start gap-3 p-3 sm:p-4 rounded-2xl border",
        "transition-all duration-200",
        styles.container,
        className,
      )}
      role="alert"
      {...props}
    >
      {/* Icon */}
      <div
        className={cn(
          "shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-xs",
          styles.iconBg,
        )}
      >
        {displayIcon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 min-h-10 flex flex-col justify-between">
        {title && (
          <h4
            className={cn(
              "font-bold text-sm tracking-tight leading-none",
              styles.title,
            )}
          >
            {title}
          </h4>
        )}
        <div
          className={cn("text-xs font-medium leading-snug", styles.description)}
        >
          {children}
        </div>
        {action && <div className="mt-3">{action}</div>}
      </div>

      {/* Dismiss Button */}
      {dismissible && onDismiss && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          className={cn(
            "shrink-0 self-start p-2 -mr-1 rounded-full active:scale-95 transition-all duration-200 cursor-pointer",
            "focus:outline-hidden focus:ring-2 focus:ring-purple-500/50",
            styles.dismiss,
          )}
          aria-label="Fechar alerta"
        >
          <X className="h-4 w-4 sm:h-5 sm:w-5 pointer-events-none" />
        </button>
      )}
    </div>
  );
};

export default Alert;
